import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtNum, fmtMoney } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, GitBranch, CircleDot } from "lucide-react";

export default function Transaction360() {
  const [spas, setSpas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/spas-with-flow").then(({ data }) => {
      setSpas(data);
      if (data.length) setSelected(data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/transaction-360/${selected}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [selected]);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900">Transaction 360°</h1>
          <p className="text-sm text-slate-500 mt-0.5">Complete petroleum lifecycle from supplier to reconciliation.</p>
        </div>
        <div className="w-72">
          <Select value={selected || ""} onValueChange={setSelected}>
            <SelectTrigger data-testid="t360-spa-select"><SelectValue placeholder="Select SPA…" /></SelectTrigger>
            <SelectContent className="bg-white z-[60]">
              {spas.map((s) => <SelectItem key={s.id} value={s.id}>{s.spa_no} · {s.supplier_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-4">
            <Card title="Commercial">
              <Field label="SPA" value={data.spa.spa_no} mono />
              <Field label="Supplier" value={data.spa.supplier_name} />
              <Field label="Product" value={data.spa.product_name} />
              <Field label="Quantity" value={`${fmtNum(data.spa.quantity)} ${data.spa.unit}`} />
              <Field label="Unit Price" value={fmtMoney(data.spa.unit_price)} />
              <Field label="Total Value" value={fmtMoney(data.spa.total_value)} strong />
              <Field label="Status" node={<StatusBadge status={data.spa.status} />} />
            </Card>
            <Card title="Financial">
              <Field label="Invoices" value={data.invoices.length} />
              <Field label="Payments" value={data.payments.length} />
              <Field label="Cost Paid" value={fmtMoney(data.payments.filter(p => p.status === "Confirmed").reduce((a, p) => a + p.amount, 0))} strong />
            </Card>
            <Card title="Operations">
              <Field label="LPRC Transfers" value={data.lprc_transfers.length} />
              <Field label="Transferred Qty" value={`${fmtNum(data.spa.transferred_qty)} L`} />
              <Field label="Reconciliations" value={data.reconciliations.length} />
            </Card>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-5">
              <GitBranch className="h-4 w-4 text-[#12346B]" />
              <h3 className="font-head text-sm font-semibold text-slate-800">Transaction Lifecycle Timeline</h3>
            </div>
            <ol className="relative border-l-2 border-slate-200 ml-2">
              {data.timeline.map((e, i) => (
                <li key={`${e.type || e.label || "step"}-${i}`} className="mb-6 ml-6" data-testid={`t360-timeline-${i}`}>
                  <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-white border-2 border-[#12346B]">
                    <CircleDot className="h-2.5 w-2.5 text-[#12346B]" />
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{e.stage}</span>
                    <span className="mono text-sm font-semibold text-slate-900">{e.reference}</span>
                    <StatusBadge status={e.status} />
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">{e.detail}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(e.at)}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);
const Field = ({ label, value, node, mono, strong }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-slate-500">{label}</span>
    {node || <span className={`${mono ? "mono" : ""} ${strong ? "font-semibold" : ""} text-slate-800`}>{value}</span>}
  </div>
);
